import api from "./api";
import type {
  AlertCountResponse,
  AlertHistoryResponse,
  AlertListResponse,
  AlertWithContext,
} from "../types/models";

export type { AlertCountResponse, AlertHistoryResponse, AlertListResponse, AlertWithContext };

/**
 * Get alert counts for nav badge and dashboard widget (Story 6.3).
 * GET /api/alerts/count – admin only.
 */
export async function getAlertCount(): Promise<AlertCountResponse> {
  const response = await api.get<AlertCountResponse>("/alerts/count");
  return response.data;
}

export interface ListAlertsParams {
  page?: number;
  limit?: number;
  status?: string | null;
  priority?: string | null;
}

/**
 * List reorder alerts with cash context (Story 6.2).
 * GET /api/alerts – admin only.
 */
export async function listAlerts(params?: ListAlertsParams): Promise<AlertListResponse> {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  const search = new URLSearchParams();
  search.set("page", String(page));
  search.set("limit", String(limit));
  if (params?.status != null && params.status !== "") search.set("status", params.status);
  if (params?.priority != null && params.priority !== "") search.set("priority", params.priority);
  const response = await api.get<AlertListResponse>(`/alerts?${search.toString()}`);
  return response.data;
}

/**
 * Acknowledge an alert (Story 6.4). PATCH /api/alerts/{id}/acknowledge – admin only.
 */
export async function acknowledgeAlert(alertId: string): Promise<void> {
  await api.patch(`/alerts/${alertId}/acknowledge`);
}

/**
 * Dismiss an alert (Story 6.4). PATCH /api/alerts/{id}/dismiss – admin only.
 */
export async function dismissAlert(alertId: string): Promise<void> {
  await api.patch(`/alerts/${alertId}/dismiss`);
}

export interface BulkAlertActionParams {
  action: "acknowledge" | "dismiss";
  alert_ids: string[];
}

export interface BulkAlertResponse {
  updated_count: number;
  alert_ids: string[];
}

/**
 * Bulk acknowledge or dismiss alerts (Story 6.4). POST /api/alerts/bulk – admin only.
 */
export async function bulkAlertAction(
  params: BulkAlertActionParams
): Promise<BulkAlertResponse> {
  const response = await api.post<BulkAlertResponse>("/alerts/bulk", params);
  return response.data;
}

export interface AlertHistoryParams {
  page?: number;
  limit?: number;
  status?: string | null;
  date_from?: string | null;
  date_to?: string | null;
}

/**
 * Get alert history – dismissed and resolved (Story 6.4). GET /api/alerts/history – admin only.
 */
export async function getAlertHistory(
  params?: AlertHistoryParams
): Promise<AlertHistoryResponse> {
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  const search = new URLSearchParams();
  search.set("page", String(page));
  search.set("limit", String(limit));
  if (params?.status != null && params.status !== "") search.set("status", params.status);
  if (params?.date_from != null && params.date_from !== "")
    search.set("date_from", params.date_from);
  if (params?.date_to != null && params.date_to !== "")
    search.set("date_to", params.date_to);
  const response = await api.get<AlertHistoryResponse>(
    `/alerts/history?${search.toString()}`
  );
  return response.data;
}
